import { GridColumnResizeParams } from '@mui/x-data-grid-pro';
import { useCallback, useRef } from 'react';

const STORAGE_KEY_PREFIX = 'columnWidths_';

export interface ColumnWidths {
  [field: string]: number;
}

function getStoredColumnWidths(
  storageKey: string | undefined,
): ColumnWidths | null {
  if (!storageKey) {
    return null;
  }
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${storageKey}`);
    if (stored) {
      return JSON.parse(stored) as ColumnWidths;
    }
  } catch {
    // Ignore localStorage errors
  }
  return null;
}

function saveColumnWidths(
  storageKey: string | undefined,
  widths: ColumnWidths,
): void {
  if (!storageKey) {
    return;
  }
  try {
    localStorage.setItem(
      `${STORAGE_KEY_PREFIX}${storageKey}`,
      JSON.stringify(widths),
    );
  } catch {
    // Ignore localStorage errors
  }
}

export interface UseColumnWidthsResult {
  /** Initial column widths loaded from localStorage (stable, won't change) */
  initialColumnWidths: ColumnWidths;
  /** Callback to save column width changes to localStorage */
  onColumnWidthChange: (params: GridColumnResizeParams) => void;
}

/**
 * Hook for persisting column widths to localStorage.
 *
 * Returns stable initial widths (won't trigger re-renders) and a callback
 * that saves width changes directly to localStorage without state updates.
 * This prevents conflicts with DataGridPro's internal column resize state.
 */
export default function useColumnWidths(
  storageKey?: string,
): UseColumnWidthsResult {
  // Use ref to store current widths in memory (for merging on save)
  // Initialize with stored values
  const widthsRef = useRef<ColumnWidths>(
    getStoredColumnWidths(storageKey) ?? {},
  );

  // Stable initial widths for column initialization (only read once)
  const initialColumnWidths = useRef<ColumnWidths>(
    getStoredColumnWidths(storageKey) ?? {},
  ).current;

  const onColumnWidthChange = useCallback(
    (params: GridColumnResizeParams) => {
      // Update ref and save directly to localStorage without triggering re-render
      widthsRef.current = {
        ...widthsRef.current,
        [params.colDef.field]: params.width,
      };
      saveColumnWidths(storageKey, widthsRef.current);
    },
    [storageKey],
  );

  return { initialColumnWidths, onColumnWidthChange };
}
